import React, { useCallback, useState } from 'react';
import { MAX_TREE_LEVEL, DEFAULT_JUICE } from './constants';
import './controls.scss';


const Tooltip = ({ title, children }) => (
  <span className="tooltip">
    {title}
    <div className="tooltip-info">
      {children}
    </div>
  </span>
);

export default ({
  totalJuice,
  setTotalJuice,
  setSelectedBranch,
  juiceLeft,
  setJuiceLeft,
  resetAll,
  sustenanceLevel,
  sorceryLevel,
  mightLevel,
  fortitudeLevel,
  celerityLevel,
  treeLevel,
  setTreeLevel,
  selectedBranch,
  save,
  load,
  removeSave,
  saveNames
}) => {
  const updateJuice = useCallback(({ target: { value: newJuice } })=>{
    if(!newJuice || newJuice < 0) newJuice=0;
    const difference = newJuice - totalJuice;
    const newJuiceLeft = juiceLeft + difference;
    if(newJuiceLeft < 0) {
      resetAll(newJuice)
    } else {
      setTotalJuice(newJuice);
      setJuiceLeft(newJuiceLeft);
    }
  }, [
    totalJuice,
    juiceLeft,
    setTotalJuice,
    setJuiceLeft,
    resetAll,
  ]);

  const useExpenditure = useCallback(() => {
    const expenditure = totalJuice - juiceLeft;
    setTotalJuice(expenditure);
    setJuiceLeft(0);
  }, [totalJuice, juiceLeft, setTotalJuice, setJuiceLeft]);

  const setReallyHigh = useCallback(() => {
    const largeAmount = DEFAULT_JUICE;
    const expenditure = totalJuice - juiceLeft;
    setTotalJuice(largeAmount);
    setJuiceLeft(largeAmount - expenditure);
  }, [setTotalJuice, setJuiceLeft, totalJuice, juiceLeft]);

  const toggleSustenance = useCallback(() => setSelectedBranch('sustenance'), [setSelectedBranch]);
  const toggleSorcery = useCallback(() => setSelectedBranch('sorcery'), [setSelectedBranch]);
  const toggleMight = useCallback(() => setSelectedBranch('might'), [setSelectedBranch]);
  const toggleFortitude = useCallback(() => setSelectedBranch('fortitude'), [setSelectedBranch]);
  const toggleCelerity = useCallback(() => setSelectedBranch('celerity'), [setSelectedBranch]);
  const toggleOverview = useCallback(() => setSelectedBranch('overview'), [setSelectedBranch]);
  const toggleOptimizer = useCallback(() => setSelectedBranch('optimizer'), [setSelectedBranch]);
  const toggleStatCalculator = useCallback(() => setSelectedBranch('statCalculator'), [setSelectedBranch]);
  const reset = useCallback(()=>resetAll(totalJuice), [totalJuice, resetAll]);

  const clampTreeLevel = useCallback((value) => {
    if(!value) value = 0;
    value = parseInt(value);
    if(value < 0) value = 0;
    if(value > MAX_TREE_LEVEL) value = MAX_TREE_LEVEL;
    const levelCap = value - 10;
    setTreeLevel(value);
    if(
         sustenanceLevel > levelCap
      || mightLevel > levelCap
      || sorceryLevel > levelCap
      || fortitudeLevel > levelCap
      || celerityLevel > levelCap
    ) resetAll(totalJuice);
  }, [
    setTreeLevel,
    resetAll,
    totalJuice,
    celerityLevel,
    sustenanceLevel,
    fortitudeLevel,
    mightLevel,
    sorceryLevel
  ]);


  const updateTreeLevel = useCallback(
    ({ target: { value } }) => clampTreeLevel(parseInt(value)),
    [clampTreeLevel],
  );

  const activeIf = useCallback((branchName) => branchName === selectedBranch ? 'active' : '', [selectedBranch]);

  const [saveName, setSaveName] = useState("");

  const updateSaveName = useCallback(({ target: { value } }) => {
    setSaveName(value);
  }, [setSaveName]);

  const activateSave = useCallback(()=>{
    if(saveName && saveName.length > 0) save(saveName);
  }, [saveName, save]);

  const [loadName, setLoadName] = useState("");

  const updateLoadName = useCallback(({ target: { value } }) => {
    setLoadName(value);
  }, [setLoadName]);

  const activateLoad = useCallback(()=>{
    if(loadName && loadName.length > 0) load(loadName);
  }, [loadName, load]);

  const activateRemove = useCallback(()=>{
    const confirmed = window.confirm(`Are you sure you want to remove ${loadName}?`);
    if(confirmed && loadName && loadName.length > 0) {
      removeSave(loadName);
      setLoadName("");
    }
  }, [removeSave, loadName, setLoadName]);
  const [juiceToAdd, setJuiceToAdd] = useState(0);
  const updateJuiceToAdd = useCallback(({ target: { value } }) => {
    setJuiceToAdd(parseInt(value));
  }, [setJuiceToAdd]);
  const addJuice = useCallback(() => {
    if(juiceToAdd) {
      setTotalJuice(parseInt(totalJuice) + parseInt(juiceToAdd));
      setJuiceLeft(parseInt(juiceLeft) + parseInt(juiceToAdd));
      setJuiceToAdd(0);
    }
  }, [setTotalJuice, setJuiceLeft, totalJuice, juiceLeft, juiceToAdd, setJuiceToAdd]);
  return (
    <div id="controls">
      <span id="persistence">
        <div id="save-controls">
          <input id="save-name" value={saveName} onChange={updateSaveName} />
          <button onClick={activateSave} disabled={!saveName || saveName.length === 0}>
            Save
          </button>
        </div>
        <div id="load-controls">
          <select id="load-name" value={loadName || ""} onChange={updateLoadName}>
            <option value="" disabled>-Select a Save-</option>
            {saveNames.map(name => <option key={name} value={name}>{name}</option>)}}
          </select>
          <button onClick={activateLoad} disabled={!loadName || loadName.length === 0}>
            Load
          </button>
          <button onClick={activateRemove} disabled={!loadName || loadName.length === 0}>
            Delete
          </button>
        </div>
      </span>
      <span id="elder-tree">
        <div id="elder-tree-input">
          <Tooltip title="Elder Tree Lv:">
            The current level of your Elder Tree.
            Your branches are capped to 10 below this.
            If you lower the level, and one of your branches exceeds the new cap, your branches will be reset and refunded.
            Buttons will be greyed out if they would cause you to exceed the max or minimum.
            {' '}{MAX_TREE_LEVEL} is the current max, and 15 is the minimum - a tree of 14 or below does not have branches unlocked.
          </Tooltip>
          <img src="images/eldertree.png" id="tree-icon" alt="elder tree" />
          <input
            id="tree-level"
            type="number"
            min="15"
            max={MAX_TREE_LEVEL}
            value={treeLevel}
            onChange={updateTreeLevel}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <span id="tree-buttons">
          <button
            disabled={parseInt(treeLevel) <= 19}
            onClick={() => clampTreeLevel(parseInt(treeLevel) - 5)}
          >
            -5
          </button>
          <button
            disabled={parseInt(treeLevel) <= 15}
            onClick={() => clampTreeLevel(parseInt(treeLevel) - 1)}
          >
            -1
          </button>
          <button
            disabled={parseInt(treeLevel) >= MAX_TREE_LEVEL}
            onClick={() => clampTreeLevel(parseInt(treeLevel) + 1)}
          >
            +1
          </button>
          <button
            disabled={parseInt(treeLevel) >= MAX_TREE_LEVEL - 4}
            onClick={() => clampTreeLevel(parseInt(treeLevel) + 5)}
          >
            +5
          </button>
        </span>
      </span>
      <span>
        <Tooltip title="Max Juice:">
          The total amount of juice available.
          The calculator will not let you spend more than this.
          If you reduce it to below your current expenditure, your branches will be reset and refunded.
        </Tooltip>
        <img src="images/essence.png" className="essence-icon" alt="essence" />
        <input
          id="max-juice"
          type="number"
          min="0"
          value={totalJuice}
          onChange={updateJuice}
          inputMode="numeric"
          pattern="[0-9]*"
        />
        <button onClick={setReallyHigh}>Set Really High</button>
      </span>
      <span id="juice-left">
        <Tooltip title="Juice Left:">
          How much juice you have left.
          You will be prevented from making upgrades that cost more than this.
        </Tooltip>
        <img src="images/essence.png" className="essence-icon" alt="essence" />
        {juiceLeft}
        <button onClick={reset}>Reset All</button>
      </span>
      <span id="add-juice-controls">
        <Tooltip title="Add Juice:">
          Input a value here (maybe amount in inventory or amount you will purchase) so you can easily add it to your total.
        </Tooltip>
        <img src="images/essence.png" className="essence-icon" alt="essence" />
        <input
          id="add-juice"
          type="number"
          value={juiceToAdd}
          onChange={updateJuiceToAdd}
          inputMode="numeric"
          pattern="[0-9]*"
        />
        <button onClick={addJuice}>Add Juice</button>
      </span>
      <span id="juice-spent">
        <Tooltip title="Juice Spent:">
          How much juice you have spent total across your branches.
          After setting your current branches, you can click the 'Set as Max' button to cap your juice to your current expenditure, so you can easily rebalance your branches with the same cost limit.
          You may want to also manually add in the amount from your bag after doing this.
        </Tooltip>
        <img src="images/essence.png" className="essence-icon" alt="essence" />
        {totalJuice - juiceLeft}
        <button onClick={useExpenditure}>Set as Max</button>
      </span>
      <span id="juice-spent">
        <Tooltip title="Days (daily shop)">
          How many days it would take you to buy the amount of juice currently spent, purchasing 600 a day from daily deals.
          This is altered slightly by the new weekly deals.
          Note that twisted realm, events, afk rewards, and the packs given on certain milestones reduce this - but it provides a benchmark.
        </Tooltip>
        {': '}{Math.ceil((totalJuice - juiceLeft) / 600)}
      </span>
      <span id="juice-spent">
        <Tooltip title="Cost (daily shop)">
          How much it would cost to buy the current juice spent in the daily deals.
          $5 usd is 150, and then $15 usd gives another 450.
          The amount available from milestones is sold at the same rate (3000 for $100 usd typically).
          This is altered slightly by the new weekly deals.
          This does not factor in twisted realm, events, or afk rewards - but it provides a benchmark for the current value.
        </Tooltip>
        {': '}${((20 * (totalJuice - juiceLeft)) / 600).toFixed(2)}
      </span>
      <span id="other-views">
        <button id="overview" onClick={toggleOverview} className={activeIf('overview')}>
          Overview
        </button>
        <button id="optimizer" onClick={toggleOptimizer} className={activeIf('optimizer')}>
          Optimizer
        </button>
        <button id="stats" onClick={toggleStatCalculator} className={activeIf('statCalculator')}>
          Stat Calculator
        </button>
      </span>
      <span id="select-branch">
        Branches:
      </span>
      <div id="branches">
        <button id="sustenance" onClick={toggleSustenance} className={activeIf('sustenance')}>
          Sustenance: {sustenanceLevel}
        </button>
        <button id="sorcery" onClick={toggleSorcery} className={activeIf('sorcery')}>  
          Sorcery: {sorceryLevel}
        </button>
        <button id="might" onClick={toggleMight} className={activeIf('might')}>     
          Might: {mightLevel}
        </button>
        <button id="fortitude" onClick={toggleFortitude} className={activeIf('fortitude')}>     
          Fortitude: {fortitudeLevel}
        </button>
        <button id="celerity" onClick={toggleCelerity} className={activeIf('celerity')}>
          Celerity: {celerityLevel}
        </button>
      </div>
    </div>
  );
};